import { showToast, Toast } from 'common/ToastMessage'
import { useEffect, useState } from 'react'
import { Spinner } from 'common/Spinner/Spinner'
import Styles from 'screens/ProseEdit/components/ProcessingSuggestionToast/ProcessingSuggestionToast.module.scss'
import { Check, Alert } from 'almanac-ui/icons'
import { ErrorBoundary } from '@sentry/react'
import { scheduledPostsKeys } from 'hooks/queries/scheduledPosts/scheduledPostsKeys'
import { usePollScheduledPostStatus } from 'hooks/queries/scheduledPosts/usePollScheduledPostStatus'
import { usePostProperties } from 'components/ArticlePostPreview/usePostProperties'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { AssigneeType, PostStatusPropertyValue } from 'api/clients/PropertyClient'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { Button } from 'blaze-ui/Button'
import { formatPostError } from 'utils/formatPostError'

interface Props {
  scheduledPost: ScheduledPostShow
}

interface ToastProps {
  isPosting?: boolean
  postingFailed?: boolean
  postUrl?: string
  message?: string
  errorMessage?: string
}

export const ProcessingPostToast = ({ isPosting, postingFailed, postUrl, message, errorMessage }: ToastProps) => {
  const openWindowSafely = useOpenWindowSafely()
  if (postingFailed) {
    return (
      <Toast.Root variant="error">
        <Toast.Icon>
          <Alert />
        </Toast.Icon>
        <Toast.Description>{errorMessage || 'Post failed to upload'}</Toast.Description>
      </Toast.Root>
    )
  }
  if (!isPosting) {
    return (
      <Toast.Root variant="success">
        <Toast.Icon>
          <Check />
        </Toast.Icon>
        <Toast.Description>Post uploaded successfully!</Toast.Description>
        {postUrl && (
          <Toast.Action>
            <Button variant="tertiary" onPress={() => openWindowSafely(postUrl)}>
              View Post
            </Button>
          </Toast.Action>
        )}
      </Toast.Root>
    )
  }

  return (
    <Toast.Root variant="generating">
      <Toast.Icon variant="generating">
        <Spinner className={Styles.spinner} variant="dark" />
      </Toast.Icon>
      <Toast.Description>{message || 'Uploading post...'}</Toast.Description>
    </Toast.Root>
  )
}

const PollScheduledPostStatus = ({ id }: { id: number }) => {
  usePollScheduledPostStatus({ id })

  return null
}

export const PollScheduledPostStatusContainer = ({ scheduledPost }: Props) => {
  const key = scheduledPostsKeys.show(scheduledPost.id ?? 0).toString()
  const [prevIsPosting, setPrevIsPosting] = useState(false)
  const isPosting = !!scheduledPost.is_posting
  const postingFailed = !!scheduledPost.posting_failed

  const { updateStatus } = usePostProperties({
    id: scheduledPost.source_id || scheduledPost.almanac_file.id,
    type: scheduledPost.source_type as AssigneeType,
  })

  useEffect(() => {
    if (isPosting && !prevIsPosting) {
      setPrevIsPosting(isPosting)
      showToast(<ProcessingPostToast isPosting />, 0, key)
    }
  }, [isPosting, key, prevIsPosting])

  useEffect(() => {
    if (prevIsPosting && !isPosting && !postingFailed) {
      showToast(<ProcessingPostToast postUrl={scheduledPost.post_url} />, 5, key)
      updateStatus(PostStatusPropertyValue.Posted)
      setPrevIsPosting(false)
    }
    if (prevIsPosting && !isPosting && postingFailed) {
      const errorMessage = formatPostError(
        scheduledPost.error_type,
        scheduledPost.external_integration?.provider,
        scheduledPost.error_message,
      )
      showToast(<ProcessingPostToast postingFailed errorMessage={errorMessage} />, 5, key)
      updateStatus(PostStatusPropertyValue.Failed)
      setPrevIsPosting(false)
    }
  }, [
    isPosting,
    key,
    postingFailed,
    prevIsPosting,
    updateStatus,
    scheduledPost.post_url,
    scheduledPost.error_message,
    scheduledPost.error_type,
    scheduledPost.external_integration?.provider,
  ])

  if (isPosting) return <PollScheduledPostStatus id={scheduledPost.id} />

  return null
}

const WithErrorBoundary = (props: Props) => (
  <ErrorBoundary>
    <PollScheduledPostStatusContainer {...props} />
  </ErrorBoundary>
)

export { WithErrorBoundary as PollScheduledPostStatus }
